import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { PH, Av, AiScoreRing, Sparkline, Bdg } from '../../components/shared';
import * as api from '../../services/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export const PageMap = memo(()=>{
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});

  useEffect(()=>{
    setLoading(true);
    api.getLeadsMap().then(data=>{
      const items = Array.isArray(data) ? data : [];
      setLeads(items);
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  // Init map
  useEffect(()=>{
    if (mapInstance.current || !mapRef.current) return;
    mapInstance.current = L.map(mapRef.current, { center:[20,5], zoom:3, maxZoom:18, zoomControl:true });
    // CartoDB Positron — clean, modern, minimal
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom:19,
      attribution:'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(mapInstance.current);
    return ()=>{ if(mapInstance.current){ mapInstance.current.remove(); mapInstance.current=null; } };
  },[]);

  // Add markers when leads load
  useEffect(()=>{
    const map = mapInstance.current;
    if (!map || leads.length===0) return;

    Object.values(markersRef.current).forEach(m=>map.removeLayer(m));
    markersRef.current = {};

    const bounds = L.latLngBounds();
    leads.forEach(lead=>{
      if (!lead.latitude || !lead.longitude) return;
      const lat = parseFloat(lead.latitude);
      const lng = parseFloat(lead.longitude);
      if (isNaN(lat)||isNaN(lng)) return;

      const initial = (lead.name||'?')[0].toUpperCase();
      const hasPhoto = !!lead.viewer_photo;
      const iconHtml = hasPhoto
        ? `<div style="width:34px;height:34px;border-radius:50%;background-image:url('${lead.viewer_photo}');background-size:cover;background-position:center;border:2.5px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,.3);"></div>`
        : `<div style="width:34px;height:34px;border-radius:50%;background:#18181b;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;border:2.5px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,.3);">${initial}</div>`;

      const icon = L.divIcon({ html:iconHtml, className:'', iconSize:[34,34], iconAnchor:[17,17] });

      const popup = `<div style="min-width:170px;font-family:system-ui,sans-serif;">
        <div style="font-weight:650;font-size:13px;margin-bottom:2px;">${lead.name||'Unknown'}</div>
        ${lead.company?`<div style="font-size:11px;color:#64748b;margin-bottom:2px;">${lead.company}</div>`:''}
        ${lead.city||lead.country?`<div style="font-size:11px;color:#94a3b8;">${[lead.city,lead.state,lead.country].filter(Boolean).join(', ')}</div>`:''}
        ${lead.email?`<div style="font-size:11px;color:#0891b2;margin-top:4px;">${lead.email}</div>`:''}
      </div>`;

      const marker = L.marker([lat,lng],{icon}).bindPopup(popup);
      marker.addTo(map);
      markersRef.current[lead.id] = marker;
      bounds.extend([lat,lng]);
    });

    if (bounds.isValid()) map.fitBounds(bounds, {padding:[50,50], maxZoom:12});
  },[leads]);

  // Resize map when layout renders
  useEffect(()=>{
    if (!loading && mapInstance.current) {
      setTimeout(()=>mapInstance.current.invalidateSize(), 100);
    }
  },[loading]);

  const handleLeadClick = useCallback((lead)=>{
    setSelected(lead.id);
    const map = mapInstance.current;
    if (!map) return;
    const lat = parseFloat(lead.latitude);
    const lng = parseFloat(lead.longitude);
    if (isNaN(lat)||isNaN(lng)) return;
    map.setView([lat,lng], 14, {animate:true});
    const marker = markersRef.current[lead.id];
    if (marker) setTimeout(()=>marker.openPopup(), 300);
  },[]);

  // Stats
  const cities = {};
  leads.forEach(l=>{ if(l.city) cities[l.city]=(cities[l.city]||0)+1; });
  const topCity = Object.entries(cities).sort((a,b)=>b[1]-a[1])[0];
  const countries = new Set(leads.map(l=>l.country).filter(Boolean));

  return (
    <div className="pg fi">
      <PH eyebrow="Leads" title="Lead Map" sub={`${leads.length} leads with location`} live/>

      {/* KPI strip */}
      <div className="g3" style={{marginBottom:18}}>
        {[
          ["📍","Total Pins",leads.length.toLocaleString(),"#64748b"],
          ["🏙","Top City",topCity?`${topCity[0]} (${topCity[1]})`:"—","#18181b"],
          ["🌍","Countries",countries.size.toString(),"#059669"],
        ].map(([ico,lbl,val,col])=>(
          <div key={lbl} className="card" style={{padding:18,display:"flex",alignItems:"center",gap:14}}>
            <div style={{fontSize:22,width:44,height:44,background:`${col}10`,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center"}}>{ico}</div>
            <div><div style={{fontSize:11.5,color:"var(--t3)",marginBottom:2}}>{lbl}</div><div style={{fontSize:20,fontWeight:720,color:col}}>{val}</div></div>
          </div>
        ))}
      </div>

      {/* Map + Leads side by side */}
      <div className="card-s" style={{overflow:"hidden",display:"flex",minHeight:540,padding:0}}>
        {/* Left — Leads list */}
        <div style={{width:320,flexShrink:0,borderRight:"1px solid rgba(0,0,0,.07)",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"14px 16px",borderBottom:"1px solid rgba(0,0,0,.07)",flexShrink:0}}>
            <div style={{fontSize:13,fontWeight:660,color:"var(--t1)"}}>Showing {leads.length} leads</div>
            <div style={{fontSize:11,color:"var(--t3)",marginTop:1}}>Click to zoom on map</div>
          </div>
          <div style={{flex:1,overflowY:"auto"}}>
            {loading ? (
              <div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:12}}>Loading...</div>
            ) : leads.length===0 ? (
              <div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:12}}>No leads with location</div>
            ) : leads.map(lead=>{
              const isSelected = selected === lead.id;
              const initial = (lead.name||'?')[0].toUpperCase();
              const location = [lead.city, lead.state, lead.country].filter(Boolean).join(', ');
              return (
                <div key={lead.id} onClick={()=>handleLeadClick(lead)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
                    cursor:"pointer",transition:"all .14s",
                    background:isSelected?"rgba(8,145,178,.07)":"transparent",
                    borderLeft:isSelected?"3px solid #0891b2":"3px solid transparent",
                    borderBottom:"1px solid rgba(0,0,0,.04)",
                  }}
                  onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background="rgba(0,0,0,.02)";}}
                  onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background=isSelected?"rgba(8,145,178,.07)":"transparent";}}>
                  {lead.viewer_photo ? (
                    <img src={lead.viewer_photo} alt="" style={{width:34,height:34,borderRadius:"50%",objectFit:"cover",flexShrink:0,border:"2px solid rgba(0,0,0,.06)"}}/>
                  ) : (
                    <div style={{width:34,height:34,borderRadius:"50%",background:"#18181b",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0}}>{initial}</div>
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:600,color:"var(--t1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.name||'Unknown'}</div>
                    <div style={{fontSize:10.5,color:"var(--t3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {location || lead.email || '—'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — Map */}
        <div style={{flex:1,position:"relative"}}>
          <div ref={mapRef} style={{height:"100%",width:"100%",minHeight:540,background:"#f8fafc"}}/>
          {!loading && leads.length > 0 && (
            <div style={{position:"absolute",top:12,right:12,zIndex:1000,padding:"6px 12px",background:"rgba(255,255,255,.92)",backdropFilter:"blur(8px)",borderRadius:10,fontSize:11,color:"var(--t3)",boxShadow:"0 2px 10px rgba(0,0,0,.1)"}}>
              Leads only show if location was allowed upon connecting
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const BOARD_COLS=[{key:"new",lbl:"New",col:"#64748b"},{key:"contacted",lbl:"Contacted",col:"#d97706"},{key:"qualified",lbl:"Qualified",col:"#0891b2"},{key:"closed",lbl:"Closed",col:"#059669"}];
const SOURCE_MAP_BOARD={1:"NFC Tap",2:"Lead Form",3:"Manual",4:"Direct"};
const ini = (n)=>{ if(!n) return '??'; const p=n.trim().split(/\s+/); return (p[0]?.[0]||'')+(p[1]?.[0]||''); };
const kpiCard={background:"rgba(255,255,255,.72)",border:"1px solid rgba(255,255,255,.9)",borderRadius:18,boxShadow:"0 4px 24px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"};
const GRADS=["linear-gradient(150deg,#64748b,#059669)","linear-gradient(150deg,#0891b2,#06b6d4)","linear-gradient(150deg,#7c3aed,#a78bfa)","linear-gradient(150deg,#d97706,#fbbf24)","linear-gradient(150deg,#dc2626,#f87171)","linear-gradient(150deg,#475569,#94a3b8)"];

export const PageLeadsBoard = memo(()=>{
  const [view, setView] = useState("leaderboard");
  const [period, setPeriod] = useState("month");
  const [board, setBoard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  /* ── load leaderboard ── */
  const loadBoard = useCallback(async(p)=>{
    setLoading(true);
    try{
      const data = await api.getDashboardLeaderboard(p);
      setBoard(data);
    }catch(e){ console.error(e); }
    finally{ setLoading(false); }
  },[]);

  /* ── load pipeline leads ── */
  const mapLead = useCallback((l)=>({
    id:l.id, name:l.name||"", company:l.company||"", email:l.email||"",
    source:SOURCE_MAP_BOARD[l.type]||"Manual", status:l.status||"new",
    aiScore:l.score||0, assignee:l.viewing_username||null,
  }),[]);
  const loadLeads = useCallback(async()=>{
    try{
      const res = await api.getLeads();
      const items = res.data||res;
      if(Array.isArray(items)) setLeads(items.map(mapLead));
    }catch(e){ console.error(e); }
  },[mapLead]);

  useEffect(()=>{ loadBoard(period); },[period, loadBoard]);
  useEffect(()=>{ if(view==="kanban" && leads.length===0) loadLeads(); },[view, leads.length, loadLeads]);

  const performers = board?.performers || [];
  const achievements = board?.achievements || [];
  const maxLeads = performers[0]?.lead_count || 1;

  /* ── drag handlers for pipeline ── */
  const handleDragStart = useCallback((e,id)=>{ setDragId(id); e.dataTransfer.effectAllowed="move"; },[]);
  const handleDragOver = useCallback((e,k)=>{ e.preventDefault(); setDragOver(k); },[]);
  const handleDragLeave = useCallback(()=>{ setDragOver(null); },[]);
  const handleDrop = useCallback(async(e,k)=>{
    e.preventDefault(); setDragOver(null);
    if(!dragId) return;
    const lead=leads.find(l=>l.id===dragId);
    if(!lead||lead.status===k){ setDragId(null); return; }
    setLeads(ls=>ls.map(l=>l.id===dragId?{...l,status:k}:l));
    setDragId(null);
    try{ await api.updateLead(dragId,{status:k}); }catch{ setLeads(ls=>ls.map(l=>l.id===dragId?{...l,status:lead.status}:l)); }
  },[dragId, leads]);
  const handleDragEnd = useCallback(()=>{ setDragId(null); setDragOver(null); },[]);

  const periodLabel = {week:'This Week', month:'This Month', quarter:'This Quarter'}[period];

  return (<div className="pg fi">
    <PH eyebrow="Gamification" title="Leaderboard" sub="Team performance rankings & achievements" live
      action={
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",background:"rgba(255,255,255,.72)",border:"1px solid rgba(255,255,255,.9)",borderRadius:10,padding:3,gap:2}}>
            {[["leaderboard","🏆 Rankings"],["kanban","📋 Pipeline"]].map(([v,l])=>(
              <button key={v} onClick={()=>setView(v)} style={{padding:"5px 12px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:"var(--f)",
                background:view===v?"#0f172a":"transparent",color:view===v?"#fff":"#64748b",
                fontSize:11.5,fontWeight:view===v?660:480,transition:"all .15s"}}>{l}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:4}}>
            {["week","month","quarter"].map(p=>(
              <button key={p} onClick={()=>setPeriod(p)} style={{padding:"5px 13px",borderRadius:8,cursor:"pointer",fontFamily:"var(--f)",
                background:period===p?"rgba(8,145,178,.1)":"transparent",color:period===p?"#0891b2":"#64748b",
                fontSize:11.5,fontWeight:period===p?660:480,transition:"all .14s",
                border:`1px solid ${period===p?"rgba(8,145,178,.2)":"transparent"}`}}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
            ))}
          </div>
        </div>
      }/>

    {/* ══════════ LEADERBOARD VIEW ══════════ */}
    {view==="leaderboard" && (<>

      {/* Achievement badges */}
      {achievements.length>0&&(
        <div style={{display:"flex",gap:10,marginBottom:18,flexWrap:"wrap"}}>
          {achievements.map(a=>(
            <div key={a.lbl} style={{...kpiCard,padding:"10px 16px",display:"flex",alignItems:"center",gap:10,flex:"1 1 180px"}}>
              <div style={{width:36,height:36,borderRadius:11,background:`${a.col}10`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{a.ico}</div>
              <div><div style={{fontSize:12,fontWeight:700,color:"var(--t1)"}}>{a.lbl}</div><div style={{fontSize:11,color:"var(--t3)",marginTop:1}}>{a.sub}</div></div>
            </div>
          ))}
        </div>
      )}

      {/* Podium top 3 */}
      {performers.length>=3&&(
        <div style={{...kpiCard,padding:"28px 32px",marginBottom:16,overflow:"hidden",position:"relative"}}>
          <div style={{position:"absolute",inset:0,background:"linear-gradient(160deg,rgba(8,145,178,.03) 0%,rgba(124,58,237,.03) 50%,rgba(5,150,105,.03) 100%)",pointerEvents:"none"}}/>
          <div style={{fontSize:11,fontWeight:700,color:"var(--t3)",letterSpacing:".1em",textTransform:"uppercase",marginBottom:20}}>🏆 {periodLabel}'s Podium</div>
          <div style={{display:"flex",gap:16,alignItems:"flex-end",justifyContent:"center"}}>
            {/* 2nd */}
            {(()=>{const m=performers[1]; const grad=GRADS[1]; return (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,flex:1,maxWidth:180}}>
                <div style={{position:"relative"}}>
                  {m.photo?<img src={m.photo} alt="" style={{width:60,height:60,borderRadius:18,objectFit:"cover",boxShadow:"0 8px 24px rgba(0,0,0,.15)"}}/>
                  :<div style={{width:60,height:60,borderRadius:18,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:"#fff",boxShadow:"0 8px 24px rgba(0,0,0,.15)"}}>{ini(m.name)}</div>}
                  <div style={{position:"absolute",top:-10,right:-10,fontSize:20}}>🥈</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--t1)"}}>{m.name}</div>
                  <div style={{fontSize:18,fontWeight:820,color:"#94a3b8",fontFamily:"var(--fd)",letterSpacing:"-1px",marginTop:4}}>{m.lead_count}</div>
                  <div style={{fontSize:10,color:"var(--t3)"}}>leads</div>
                </div>
                <div style={{width:"100%",height:60,background:"linear-gradient(180deg,rgba(148,163,184,.15),rgba(148,163,184,.08))",borderRadius:"10px 10px 0 0",border:"1px solid rgba(148,163,184,.2)",borderBottom:"none"}}/>
              </div>
            );})()}
            {/* 1st */}
            {(()=>{const m=performers[0]; const grad=GRADS[0]; return (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,flex:1,maxWidth:200}}>
                <div style={{position:"relative"}}>
                  {m.photo?<img src={m.photo} alt="" style={{width:72,height:72,borderRadius:22,objectFit:"cover",boxShadow:"0 12px 32px rgba(0,0,0,.2)",border:"3px solid rgba(217,119,6,.4)"}}/>
                  :<div style={{width:72,height:72,borderRadius:22,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,fontWeight:800,color:"#fff",boxShadow:"0 12px 32px rgba(0,0,0,.2)",border:"3px solid rgba(217,119,6,.4)"}}>{ini(m.name)}</div>}
                  <div style={{position:"absolute",top:-14,right:-10,fontSize:24}}>🥇</div>
                  <div style={{position:"absolute",inset:-4,borderRadius:26,background:"linear-gradient(135deg,rgba(217,119,6,.15),rgba(251,191,36,.1))",zIndex:-1}}/>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:14,fontWeight:800,color:"var(--t1)"}}>{m.name}</div>
                  <div style={{fontSize:24,fontWeight:820,color:"#d97706",fontFamily:"var(--fd)",letterSpacing:"-1px",marginTop:4}}>{m.lead_count}</div>
                  <div style={{fontSize:10.5,color:"var(--t3)"}}>leads captured</div>
                </div>
                <div style={{width:"100%",height:90,background:"linear-gradient(180deg,rgba(217,119,6,.12),rgba(217,119,6,.05))",borderRadius:"10px 10px 0 0",border:"1px solid rgba(217,119,6,.2)",borderBottom:"none"}}/>
              </div>
            );})()}
            {/* 3rd */}
            {(()=>{const m=performers[2]; const grad=GRADS[2]; return (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,flex:1,maxWidth:180}}>
                <div style={{position:"relative"}}>
                  {m.photo?<img src={m.photo} alt="" style={{width:60,height:60,borderRadius:18,objectFit:"cover",boxShadow:"0 8px 24px rgba(0,0,0,.15)"}}/>
                  :<div style={{width:60,height:60,borderRadius:18,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:800,color:"#fff",boxShadow:"0 8px 24px rgba(0,0,0,.15)"}}>{ini(m.name)}</div>}
                  <div style={{position:"absolute",top:-10,right:-10,fontSize:20}}>🥉</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--t1)"}}>{m.name}</div>
                  <div style={{fontSize:18,fontWeight:820,color:"#b45309",fontFamily:"var(--fd)",letterSpacing:"-1px",marginTop:4}}>{m.lead_count}</div>
                  <div style={{fontSize:10,color:"var(--t3)"}}>leads</div>
                </div>
                <div style={{width:"100%",height:44,background:"linear-gradient(180deg,rgba(180,83,9,.1),rgba(180,83,9,.04))",borderRadius:"10px 10px 0 0",border:"1px solid rgba(180,83,9,.15)",borderBottom:"none"}}/>
              </div>
            );})()}
          </div>
        </div>
      )}

      {/* Full rankings table */}
      <div style={{...kpiCard,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"44px 1fr 80px 80px 70px 80px",gap:0,padding:"10px 20px",borderBottom:"1px solid rgba(0,0,0,.06)",background:"rgba(0,0,0,.02)"}}>
          {["#","Member","Leads","Taps","Conv.","AI Score"].map(h=>(
            <div key={h} style={{fontSize:10.5,fontWeight:700,color:"var(--t3)",letterSpacing:".06em",textTransform:"uppercase",textAlign:h==="#"||h==="Member"?"left":"center"}}>{h}</div>
          ))}
        </div>
        {loading&&<div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:12}}>Loading…</div>}
        {!loading&&performers.length===0&&<div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:12}}>No data for this period</div>}
        {performers.map((m,i)=>{
          const grad=GRADS[i%GRADS.length];
          return (
          <div key={m.id} style={{
            display:"grid",gridTemplateColumns:"44px 1fr 80px 80px 70px 80px",
            gap:0,padding:"13px 20px",
            borderBottom:i<performers.length-1?"1px solid rgba(0,0,0,.045)":"none",
            alignItems:"center",
            background:i===0?"rgba(217,119,6,.025)":i===1?"rgba(148,163,184,.015)":"transparent",
            transition:"background .15s",cursor:"default",
          }}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{width:26,height:26,borderRadius:8,background:i<3?"transparent":"rgba(0,0,0,.04)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:i<3?18:12,fontWeight:800}}>
                {i<3?["🥇","🥈","🥉"][i]:m.rank}
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:11}}>
              {m.photo?<img src={m.photo} alt="" style={{width:34,height:34,borderRadius:10,objectFit:"cover",flexShrink:0,boxShadow:"0 3px 10px rgba(0,0,0,.14)"}}/>
              :<div style={{width:34,height:34,borderRadius:10,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11.5,fontWeight:800,color:"#fff",flexShrink:0,boxShadow:"0 3px 10px rgba(0,0,0,.14)"}}>{ini(m.name)}</div>}
              <div style={{minWidth:0}}>
                <div style={{fontSize:13,fontWeight:660,color:"var(--t1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name||m.username}</div>
              </div>
            </div>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:15,fontWeight:800,color:"var(--t1)",letterSpacing:"-0.5px"}}>{m.lead_count}</div>
              <div style={{height:3,borderRadius:2,background:"rgba(0,0,0,.06)",marginTop:3,maxWidth:50,margin:"3px auto 0"}}>
                <div style={{height:"100%",borderRadius:2,background:grad.match(/#[a-f0-9]+/i)?.[0]||"#64748b",width:`${(m.lead_count/maxLeads)*100}%`}}/>
              </div>
            </div>
            <div style={{textAlign:"center",fontSize:13,fontWeight:640,color:"#0891b2"}}>{(m.taps||0).toLocaleString()}</div>
            <div style={{textAlign:"center",fontSize:13,fontWeight:640,color:"#059669"}}>{m.conv_rate}</div>
            <div style={{display:"flex",justifyContent:"center"}}><AiScoreRing score={m.avg_ai_score||0} size={32}/></div>
          </div>
        );})}
      </div>
    </>)}

    {/* ══════════ PIPELINE / KANBAN VIEW ══════════ */}
    {view==="kanban" && (
      <div className="kb">{BOARD_COLS.map(col=>{
        const items=leads.filter(l=>l.status===col.key);
        const isOver=dragOver===col.key;
        return (<div key={col.key} className="kbc"
          onDragOver={e=>handleDragOver(e,col.key)} onDragLeave={handleDragLeave} onDrop={e=>handleDrop(e,col.key)}
          style={isOver?{background:"rgba(8,145,178,.06)",borderColor:"rgba(8,145,178,.25)"}:undefined}>
          <div className="kbh"><span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:8,height:8,borderRadius:"50%",background:col.col,boxShadow:`0 0 5px ${col.col}80`,display:"inline-block"}}/>{col.lbl}</span><span className="kbcnt">{items.length}</span></div>
          <div className="kbc-body">
            {items.length===0 && <div style={{padding:"20px 0",textAlign:"center",fontSize:11,color:"var(--t3)"}}>No leads</div>}
            {items.map(l=>(
              <div key={l.id} className="kcard" draggable
                onDragStart={e=>handleDragStart(e,l.id)} onDragEnd={handleDragEnd}
                style={dragId===l.id?{opacity:.45}:undefined}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:4}}>
                  <div className="kcard-n">{l.name}</div>
                  <AiScoreRing score={l.aiScore} size={28}/>
                </div>
                <div className="kcard-c">{l.company}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span className="badge b-blue" style={{fontSize:9.5}}>{l.source}</span>
                  {l.assignee&&<span style={{fontSize:10,color:"var(--t3)"}}>{l.assignee.split(" ")[0]}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>);
      })}</div>
    )}
  </div>);
});

export const PageInsights = memo(()=>{
  const [period, setPeriod] = useState("month");
  const [animDone, setAnimDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [sources, setSources] = useState({ data: [], total: 0 });
  const [team, setTeam] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(()=>{ const t=setTimeout(()=>setAnimDone(true),200); return()=>clearTimeout(t); },[]);

  // Load all data
  useEffect(()=>{
    setLoading(true);
    Promise.all([
      api.getDashboardStats().catch(()=>({})),
      api.getDashboardFunnel().catch(()=>({data:[]})),
      api.getDashboardLeadSources().catch(()=>({data:[],total:0})),
      api.getDashboardTopPerformers().catch(()=>({data:[]})),
      api.getDashboardActivityHeatmap(period).catch(()=>null),
      api.getDashboardTrendOverview(period).catch(()=>({data:[]})),
    ]).then(([st, fn, sr, tp, hm, tr])=>{
      setStats(st);
      setFunnel(fn.data||[]);
      setSources(sr);
      setTeam((tp.data||[]).slice(0,6));
      setHeatmap(hm);
      setTrend(tr.data||[]);
    }).finally(()=>setLoading(false));
  },[period]);

  const s = stats || {};
  const funnelMax = Math.max(...funnel.map(f=>f.count),1);
  const maxLeads = Math.max(...team.map(t=>t.lead_count),1);
  const scoreCol = sc => sc>=80?"#059669":sc>=50?"#d97706":"#dc2626";

  // Heatmap
  const hMatrix = heatmap?.matrix || [];
  const hDays = heatmap?.dayNames || ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const hHours = heatmap?.hourLabels || ["6a","9a","12p","3p","6p","9p"];
  const hMax = Math.max(...hMatrix.flat(),1);

  // Source donut
  const srcData = (sources.data||[]).map(s=>({
    label: s.label, count: s.count, pct: s.pct,
    col: {1:"#0891b2",2:"#d97706",3:"#64748b",4:"#7c3aed"}[s.type]||"#94a3b8",
  }));

  const card = {background:"rgba(255,255,255,.72)",border:"1px solid rgba(255,255,255,.9)",borderRadius:18,boxShadow:"0 4px 24px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"};

  return (
    <div className="pg fi">
      <PH eyebrow="Analytics" title="Analytics & Reports" sub={loading?"Loading…":"AI-powered insights"} live
        action={
          <div style={{display:"flex",gap:5}}>
            {["week","month","quarter"].map(p=>(
              <button key={p} onClick={()=>setPeriod(p)} style={{
                padding:"6px 14px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"var(--f)",
                background:period===p?"#0f172a":"rgba(255,255,255,.72)",
                color:period===p?"#fff":"#64748b",fontSize:12,
                fontWeight:period===p?660:480,
                boxShadow:period===p?"0 4px 12px rgba(0,0,0,.15)":"0 2px 6px rgba(0,0,0,.04)",
                border:period===p?"none":"1px solid rgba(255,255,255,.9)",transition:"all .15s",
              }}>{p.charAt(0).toUpperCase()+p.slice(1)}</button>
            ))}
          </div>
        }
      />

      {/* ── KPI row ── */}
      <div className="g4" style={{marginBottom:20}}>
        {[
          {l:"Total Leads",   v:(s.totalLeads||0).toLocaleString(), col:"#0891b2", ch:s.leadsChange?`${s.leadsChange>0?"↑":"↓"} ${Math.abs(s.leadsChange)}%`:"—"},
          {l:"Avg AI Score",  v:String(s.avgAiScore||0),            col:"#7c3aed", ch:"—"},
          {l:"Profile Views", v:(s.totalViews||0).toLocaleString(), col:"#18181b", ch:"—"},
          {l:"Total Taps",    v:(s.totalTaps||0).toLocaleString(),  col:"#059669", ch:"—"},
        ].map(({l,v,col,ch},i)=>(
          <div key={l} className={`fi s${i+1}`} style={{...card,padding:"18px 20px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:-18,right:-18,width:80,height:80,borderRadius:"50%",background:`${col}08`,pointerEvents:"none"}}/>
            <div style={{fontSize:11,color:"var(--t3)",marginBottom:6,fontWeight:540,textTransform:"uppercase",letterSpacing:".06em"}}>{l}</div>
            <div style={{fontFamily:"var(--fd)",fontSize:28,color:col,letterSpacing:"-1px",lineHeight:1,marginBottom:8}}>{v}</div>
            <span style={{fontSize:11,color:ch.startsWith("↑")?"#059669":ch.startsWith("↓")?"#dc2626":"var(--t3)",fontWeight:660}}>{ch} {ch!=="—"?`vs last ${period}`:""}</span>
          </div>
        ))}
      </div>

      {/* ── Trend Overview ── */}
      {(() => {
        // Fixed labels per period
        const LABELS = {
          week: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
          month: ["W1","W2","W3","W4"],
          quarter: ["Jan","Feb","Mar"],
        };
        const labels = LABELS[period] || LABELS.month;

        // Map backend data to fixed labels, filling zeros for missing
        const trendMap = {};
        trend.forEach(d => { trendMap[d.label] = d; });
        const chartData = labels.map(lbl => ({
          label: lbl,
          taps: Number(trendMap[lbl]?.taps) || 0,
          leads: Number(trendMap[lbl]?.leads) || 0,
          conv: Number(trendMap[lbl]?.conv) || 0,
        }));

        const W = 520, H = 180, PAD = 36;
        const n = chartData.length;
        const keys = ["taps", "leads", "conv"];
        const colors = { taps: "#0891b2", leads: "#7c3aed", conv: "#059669" };

        // Find global max across all series for shared Y scale
        const allVals = chartData.flatMap(d => [d.taps, d.leads, d.conv]);
        const globalMax = Math.max(...allVals, 1);

        const mkLine = (key) => {
          const col = colors[key];
          const pts = chartData.map((d, i) => {
            const x = PAD + (i / (n - 1)) * (W - PAD * 2);
            const y = H - PAD - (d[key] / globalMax) * (H - PAD * 2);
            return [x, y];
          });
          const ptsStr = pts.map(p => p.join(",")).join(" ");
          const areaStr = `${pts[0][0]},${H - PAD} ${ptsStr} ${pts[n - 1][0]},${H - PAD}`;
          return { pts, ptsStr, areaStr, col };
        };

        const lines = keys.map(mkLine);

        // Y-axis labels
        const yTicks = [0, .25, .5, .75, 1];
        const yLabels = yTicks.map(t => Math.round(globalMax * (1 - t)));

        return (
          <div style={{ ...card, padding: "22px 24px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t1)" }}>Trend Overview</div>
                <div style={{ fontSize: 11.5, color: "var(--t3)", marginTop: 2 }}>Taps · Leads · Conversions</div>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                {[["#0891b2", "Taps"], ["#7c3aed", "Leads"], ["#059669", "Conv."]].map(([c, l]) => (
                  <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--t2)", fontWeight: 540 }}>
                    <div style={{ width: 26, height: 3, borderRadius: 2, background: c }} />
                    {l}
                  </div>
                ))}
              </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H + 20}`} style={{ width: "100%", height: "auto", overflow: "visible" }}>
              <defs>
                {keys.map((key, ki) => (
                  <linearGradient key={key} id={`ig_${key}_${period}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lines[ki].col} stopOpacity=".16" />
                    <stop offset="100%" stopColor={lines[ki].col} stopOpacity=".01" />
                  </linearGradient>
                ))}
                <clipPath id={`trendClip_${period}`}>
                  <rect x={PAD} y={0} width={animDone ? W - PAD * 2 : 0} height={H} style={{ transition: "width 1.4s cubic-bezier(.16,1,.3,1)" }} />
                </clipPath>
              </defs>

              {/* Grid lines + Y labels */}
              {yTicks.map((t, ti) => {
                const y = PAD + t * (H - PAD * 2);
                return (
                  <g key={t}>
                    <line x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="rgba(0,0,0,.06)" strokeWidth="1" />
                    <text x={PAD - 6} y={y + 3.5} textAnchor="end" style={{ fontSize: 9, fill: "#cbd5e1", fontFamily: "var(--f)" }}>{yLabels[ti]}</text>
                  </g>
                );
              })}

              {/* X-axis labels */}
              {chartData.map((d, i) => {
                const x = PAD + (i / (n - 1)) * (W - PAD * 2);
                return <text key={d.label} x={x} y={H + 12} textAnchor="middle" style={{ fontSize: 10.5, fill: "#94a3b8", fontFamily: "var(--f)", fontWeight: 500 }}>{d.label}</text>;
              })}

              {/* Area fills + polylines */}
              {lines.map((line, li) => (
                <g key={keys[li]} clipPath={`url(#trendClip_${period})`}>
                  <polygon points={line.areaStr} fill={`url(#ig_${keys[li]}_${period})`} />
                  <polyline points={line.ptsStr} fill="none" stroke={line.col} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </g>
              ))}

              {/* Data point dots */}
              {lines.map((line, li) =>
                line.pts.map(([px, py], pi) => (
                  <circle key={`${li}-${pi}`} cx={px} cy={py} r={pi === n - 1 ? 5 : 3.5}
                    fill={line.col} stroke="#fff" strokeWidth={pi === n - 1 ? 2.5 : 1.5}
                    opacity={animDone ? 1 : 0}
                    style={{ transition: `opacity .3s ${(.8 + pi * .1).toFixed(1)}s` }} />
                ))
              )}
            </svg>
          </div>
        );
      })()}

      {/* ── Funnel + Sources ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:16,marginBottom:16}}>

        {/* Conversion Funnel */}
        <div style={{...card,padding:"22px 24px"}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--t1)",marginBottom:4}}>Conversion Funnel</div>
          <div style={{fontSize:11.5,color:"var(--t3)",marginBottom:16}}>Full pipeline · {period}</div>
          {loading?<div style={{padding:20,textAlign:"center",fontSize:12,color:"var(--t3)"}}>Loading…</div>:
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {funnel.map((f,i)=>{
              const cols = ["#0891b2","#7c3aed","#d97706","#059669","#dc2626"];
              const col = cols[i%cols.length];
              const pct = f.pct||0;
              const cvr = i===0?"100%":`${pct}%`;
              return (
                <div key={f.stage}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,color:"var(--t2)",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:14}}>{f.ico}</span>
                      {f.stage}
                    </span>
                    <div style={{display:"flex",gap:10,alignItems:"center"}}>
                      {i>0&&<span style={{fontSize:10.5,color:col,fontWeight:700,background:`${col}10`,padding:"1px 7px",borderRadius:20}}>{cvr}</span>}
                      <span style={{fontSize:12.5,fontWeight:700,color:"var(--t1)"}}>{f.count.toLocaleString()}</span>
                    </div>
                  </div>
                  <div style={{height:8,borderRadius:4,background:"rgba(0,0,0,.05)",overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,${col},${col}88)`,
                      width:animDone?`${(f.count/funnelMax)*100}%`:"0%",transition:`width 1s cubic-bezier(.16,1,.3,1) ${i*.1}s`}}/>
                  </div>
                </div>
              );
            })}
          </div>}
          {!loading&&funnel.length>=2&&(
            <div style={{marginTop:16,padding:"10px 14px",borderRadius:12,background:"rgba(5,150,105,.06)",border:"1px solid rgba(5,150,105,.15)"}}>
              <div style={{fontSize:11,color:"var(--t3)",marginBottom:2}}>Overall conversion</div>
              <div style={{fontSize:20,fontWeight:800,color:"#059669",letterSpacing:"-1px"}}>
                {funnelMax>0?((funnel[funnel.length-1]?.count||0)/funnelMax*100).toFixed(1):"0"}%
                <span style={{fontSize:11,fontWeight:400,color:"var(--t3)",marginLeft:6}}>capture → close</span>
              </div>
            </div>
          )}
        </div>

        {/* Lead Sources */}
        <div style={{...card,padding:"22px 24px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--t1)"}}>Lead Sources</div>
            <span style={{fontSize:11,color:"#0891b2",fontWeight:660}}>{(sources.total||0).toLocaleString()} total</span>
          </div>
          {loading?<div style={{padding:20,textAlign:"center",fontSize:12,color:"var(--t3)"}}>Loading…</div>:
          srcData.length===0?<div style={{padding:20,textAlign:"center",fontSize:12,color:"var(--t3)"}}>No data</div>:
          <>
            <svg width="100" height="100" viewBox="0 0 100 100" style={{display:"block",margin:"0 auto 16px"}}>
              {(()=>{
                const r=38,cx=50,cy=50,circ=2*Math.PI*r; let off=0;
                return srcData.map(({pct,col},i)=>{
                  const dash=(pct/100)*circ,gap=circ-dash;
                  const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth="14"
                    strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-off}
                    transform={`rotate(-90 ${cx} ${cy})`}/>;
                  off+=dash; return el;
                });
              })()}
              <text x="50" y="47" textAnchor="middle" style={{fontSize:13,fontWeight:800,fill:"#0f172a",fontFamily:"var(--f)"}}>{sources.total||0}</text>
              <text x="50" y="58" textAnchor="middle" style={{fontSize:8,fill:"#94a3b8",fontFamily:"var(--f)"}}>leads</text>
            </svg>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {srcData.map(({label,count,pct,col})=>(
                <div key={label} style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:10,height:10,borderRadius:3,background:col,flexShrink:0}}/>
                  <div style={{flex:1,fontSize:12,color:"var(--t1)",fontWeight:560}}>{label}</div>
                  <span style={{fontSize:11,color:"var(--t3)"}}>{count}</span>
                  <span style={{fontSize:12,fontWeight:700,color:col,minWidth:36,textAlign:"right"}}>{pct}%</span>
                </div>
              ))}
            </div>
          </>}
        </div>
      </div>

      {/* ── Heatmap + Team performance ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:16,marginBottom:16}}>

        {/* NFC Tap Heatmap */}
        <div style={{...card,padding:"22px 24px"}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--t1)",marginBottom:3}}>NFC Tap Heatmap</div>
          <div style={{fontSize:11.5,color:"var(--t3)",marginBottom:16}}>Tap activity by day & hour · {period}
            {heatmap?.peak?.count>0&&<span style={{marginLeft:8,color:"#0891b2",fontWeight:600}}>Peak: {heatmap.peak.day} {heatmap.peak.hour} ({heatmap.peak.count} taps)</span>}
          </div>
          {loading||hMatrix.length===0?<div style={{padding:30,textAlign:"center",fontSize:12,color:"var(--t3)"}}>Loading…</div>:
          <>
          <div style={{display:"flex",gap:0}}>
            <div style={{display:"flex",flexDirection:"column",gap:3,marginRight:8,paddingTop:12}}>
              {hDays.map(d=>(
                <div key={d} style={{height:18,fontSize:9.5,color:"#94a3b8",display:"flex",alignItems:"center",fontWeight:500}}>{d}</div>
              ))}
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                {hHours.map(h=>(
                  <div key={h} style={{fontSize:8.5,color:"#94a3b8",flex:1,textAlign:"center"}}>{h}</div>
                ))}
              </div>
              {/* Matrix is hourSlots × days, we need to transpose: show days as rows */}
              {hDays.map((day,di)=>(
                <div key={di} style={{display:"flex",gap:3,marginBottom:3}}>
                  {hMatrix.map((hourRow,hi)=>{
                    const v = hourRow[di]||0;
                    const intensity = hMax>0?v/hMax:0;
                    const bg = intensity<.1?"rgba(8,145,178,.04)"
                              :intensity<.25?"rgba(8,145,178,.15)"
                              :intensity<.5?"rgba(8,145,178,.35)"
                              :intensity<.75?"rgba(8,145,178,.58)"
                              :"rgba(8,145,178,.85)";
                    return (
                      <div key={hi} title={`${day} ${hHours[hi]||""}: ${v} taps`}
                        style={{flex:1,height:18,borderRadius:3,background:bg,
                          transition:`opacity .3s ${(di*.07+hi*.02).toFixed(2)}s`,
                          opacity:animDone?1:0,cursor:"pointer"}}/>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10}}>
            <span style={{fontSize:10,color:"#94a3b8"}}>Less</span>
            {[.04,.15,.35,.58,.85].map(o=>(
              <div key={o} style={{width:14,height:14,borderRadius:3,background:`rgba(8,145,178,${o})`}}/>
            ))}
            <span style={{fontSize:10,color:"#94a3b8"}}>More</span>
          </div>
          </>}
        </div>

        {/* Team performance */}
        <div style={{...card,padding:"22px 24px"}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--t1)",marginBottom:3}}>Team Performance</div>
          <div style={{fontSize:11.5,color:"var(--t3)",marginBottom:16}}>Ranked by lead count</div>
          {loading?<div style={{padding:20,textAlign:"center",fontSize:12,color:"var(--t3)"}}>Loading…</div>:
          team.length===0?<div style={{padding:20,textAlign:"center",fontSize:12,color:"var(--t3)"}}>No team data</div>:
          <div style={{display:"flex",flexDirection:"column",gap:13}}>
            {team.map((m,i)=>{
              const initial = (m.name||m.username||"?").charAt(0).toUpperCase();
              const sc = m.avg_ai_score||0;
              return (
                <div key={m.id} style={{display:"flex",alignItems:"center",gap:11}}>
                  <div style={{position:"relative",flexShrink:0}}>
                    {m.photo
                      ? <img src={m.photo} alt="" style={{width:30,height:30,borderRadius:"50%",objectFit:"cover"}}/>
                      : <Av name={initial+initial} grad="linear-gradient(150deg,#64748b,#059669)" size={30}/>
                    }
                    {i<3&&<div style={{position:"absolute",top:-5,left:-5,width:14,height:14,borderRadius:"50%",
                      background:["#d97706","#94a3b8","#b45309"][i],
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:7,color:"#fff",fontWeight:900}}>
                      {i+1}
                    </div>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                      <span style={{fontWeight:600,color:"var(--t1)"}}>{m.name||m.username}</span>
                      <span style={{color:"#0891b2",fontWeight:700}}>{m.lead_count}</span>
                    </div>
                    <div style={{height:5,borderRadius:3,background:"rgba(0,0,0,.06)",overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:3,background:"linear-gradient(90deg,#0891b2,#06b6d4)",
                        width:animDone?`${(m.lead_count/maxLeads)*100}%`:"0%",
                        transition:`width 1s cubic-bezier(.16,1,.3,1) ${i*.08}s`}}/>
                    </div>
                  </div>
                  <AiScoreRing score={sc} size={30}/>
                </div>
              );
            })}
          </div>}
        </div>
      </div>
    </div>
  );
});

